import React, { useMemo } from 'react';

import { CorporateAccount, sessionQuery } from '@lib/state';
import {
  Section,
  useObservable,
  usePropertyRoomTypes,
  ButtonLink,
  usePropertyAmenities,
  LoadingIndicator,
} from '@lib/common';
import { RatePlanList } from 'app/room-rate-plans';
import { mapRoomRatePlan, RoomRatePlan } from 'app/state';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { RoomTypeModel } from '@lib/state/api/generated/properties';
import { parseISO } from 'date-fns';

interface Props {
  account?: CorporateAccount;
}

export const CorporateAccountRatePlans: React.FC<Props> = ({ account }) => {
  const propertyId = useObservable(sessionQuery.propertyId, 'async');
  const propertyAmenities = usePropertyAmenities(propertyId);
  const roomTypes = usePropertyRoomTypes(propertyId);
  const today = new Date();

  const [selectedRoomType, setSelectedRoomType] = React.useState<RoomTypeModel | null>(null);
  const [hideExpiredPlans, setHideExpiredPlans] = React.useState(false);
  const [sortOrder, setSortOrder] = React.useState<'asc' | 'desc'>('desc');
  const sortOptions = [
    { label: 'End Date Desc', value: 'desc' },
    { label: 'End Date Asc', value: 'asc' },
  ];

  //filter rate plans based on property
  const ratePlans = useMemo(() => {
    if (!account?.ratePlans || !propertyId) return new Array<RoomRatePlan>();

    return account.ratePlans
      .filter(plan => plan.propertyId === propertyId)
      .filter(plan => !selectedRoomType || plan.roomTypeId === selectedRoomType.id)
      .filter(plan => !hideExpiredPlans || !plan.endDate || parseISO(plan.endDate) >= today)
      .sort((a, b) => {
        const aDate = a.endDate ? parseISO(a.endDate).getTime() : 0;
        const bDate = b.endDate ? parseISO(b.endDate).getTime() : 0;
        return sortOrder === 'asc' ? aDate - bDate : bDate - aDate;
      })
      .map(plan => mapRoomRatePlan(plan) as RoomRatePlan);
    // eslint-disable-next-line
  }, [account?.ratePlans, propertyId, selectedRoomType, hideExpiredPlans, sortOrder]);

  const link = useMemo(() => {
    if (!account || !propertyId) return '';

    return `/rates/add?corporateAccountId=${account.id}`;
  }, [account, propertyId]);

  if (!account || !propertyId) return null;

  if (!roomTypes.length || !propertyAmenities) return <LoadingIndicator />;

  return (
    <Section title="Account Rate Plans" textAlign="left">
      <Grid container spacing={2} style={{ marginBottom: '1rem' }}>
        <Grid item md={4} xs={12}>
          <InputLabel id="endDateLabel" style={{ fontSize: '.88em', marginTop: '-.2em' }}>
            Sort End Date
          </InputLabel>
          <Select
            name="endDateSort"
            labelId="endDateLabel"
            fullWidth
            onChange={e => setSortOrder(e.target.value as 'asc' | 'desc')}
            value={sortOrder}
            inputProps={{
              IconComponent: (props: any) => (
                <FontAwesomeIcon
                  icon="chevron-down"
                  transform="down-6 left-4"
                  fixedWidth
                  {...props}
                />
              ),
            }}
          >
            {sortOptions.map((option, i) => (
              <MenuItem key={i} value={option.value} dense>
                <Typography>{option.label}</Typography>
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item md={4} xs={12}>
          <InputLabel id="roomTypeLabel" style={{ fontSize: '.88em', marginTop: '-.2em' }}>
            Room Type
          </InputLabel>
          <Select
            name="roomType"
            labelId="roomTypeLabel"
            fullWidth
            onChange={e => {
              const selected = roomTypes.find(rt => rt.id === e.target.value) || null;
              setSelectedRoomType(selected);
            }}
            value={selectedRoomType?.id ?? 'All'}
            inputProps={{
              IconComponent: (props: any) => (
                <FontAwesomeIcon
                  icon="chevron-down"
                  transform="down-6 left-4"
                  fixedWidth
                  {...props}
                />
              ),
            }}
          >
            <MenuItem value="All" dense>
              <Typography>All</Typography>
            </MenuItem>
            {roomTypes.map((x, i) => (
              <MenuItem key={i} value={x.id} dense>
                <Typography>{x.name}</Typography>
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item md={2} xs={12}>
          <FormControlLabel
            label="Hide Expired Plans"
            control={
              <Checkbox
                name="hideExpiredPlans"
                title="Hide Expired Plans"
                onChange={e => {
                  setHideExpiredPlans(e.target.checked);
                }}
              />
            }
          />
        </Grid>
      </Grid>
      <Box my="1rem" display="flex" justifyContent="flex-end" flexDirection="row">
        <ButtonLink
          to={link}
          variant="contained"
          color="secondary"
          data-testid="corporateNewRatePlanButton"
        >
          New Rate Plan
        </ButtonLink>
      </Box>
      <RatePlanList
        plans={ratePlans}
        roomTypes={roomTypes}
        propertyAmenities={propertyAmenities}
        account={account}
      />
    </Section>
  );
};
